import React from 'react'
import PropTypes from 'prop-types'
import styled from 'react-emotion'
import themeVars from 'ui_theme/site/globals/site.variables'
import { space as spacing, themeGet as th } from 'styled-system'

import { keys } from 'util/objects'

import Tooltip from './Tooltip'
import Icon from './Icon'
import SVGIcon from './SVGIcon'
import { colors, colorPropIf, colorPropUnless, colorProp } from './theme'

const mapIconSize = size => {
  if (size === 'tiny') return 'xsmall'
  return size
}

const StyledIcon = styled(Icon)`
  transition: color 175ms ease-in-out;
`

const StyledSVGIcon = styled(SVGIcon)`
  transition: color 175ms ease-in-out;
`

const StyledLabel = styled('div')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 110px;
  min-width: 0;

  font-family: ${themeVars.walsheim};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 2px;

  margin: 8px 12px;
  margin-right: 10px; // default right margin - letter spacing
`

const IconButton = ({
  active,
  as: ElementType,
  bgColor, // dont passthru
  children,
  className,
  color,
  name,
  bordered, // dont passthru
  size,
  iconSize,
  svg,
  label,
  onlyIcon,
  ...rest
}) => {
  const icon = svg ? (
    <StyledSVGIcon
      color={active ? color : undefined}
      name={name}
      size={iconSize}
    />
  ) : (
    <StyledIcon
      color={active ? color : 'gray'}
      name={name}
      size={iconSize || mapIconSize(size)}
    />
  )

  return (
    <ElementType className={className} {...rest}>
      {label && <StyledLabel>{label}</StyledLabel>}
      {children && children}
      {!children && !label && icon}
    </ElementType>
  )
}

IconButton.propTypes = {
  // Shows a highlighted state  of the button (i.e. outlined)
  active: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types

  /* Type of component to render. E.g. 'span', or Link. False wil render div */
  as: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.symbol,
    PropTypes.bool,
    PropTypes.object,
  ]),

  /* Set a background color (omitting defaults to `inherit`) */
  bgColor: PropTypes.oneOf(keys(colors)),

  /* Whether to render button with border */
  bordered: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types

  color: PropTypes.oneOf(keys(colors)).isRequired,

  /* name of the corresponding .svg file in assets/icons */
  name: PropTypes.string.isRequired,

  size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),

  label: PropTypes.string,
}

IconButton.defaultProps = {
  active: false,
  as: 'div',
  bgColor: undefined,
  bordered: false,
  color: 'blue',
  size: 'medium',
  label: undefined,
}

const sizes = ({ size, theme: { fontSizes, inputHeights } }) => {
  const tiny = {
    fontSize: `${fontSizes[2]}px`,
    // apparently 24px is a non-standard input height (?!)...
    height: '24px',
    width: '24px',
  }

  const small = {
    fontSize: `${fontSizes[3]}px`,
    height: `${inputHeights[0]}px`,
    width: `${inputHeights[0]}px`,
  }

  const medium = {
    fontSize: `${fontSizes[4]}px`,
    height: `${inputHeights[1]}px`,
    width: `${inputHeights[1]}px`,
  }

  const large = {
    fontSize: `${fontSizes[5]}px`,
    height: `${inputHeights[2]}px`,
    width: `${inputHeights[2]}px`,
  }

  switch (size) {
    case 'tiny':
      return tiny
    case 'small':
      return small
    case 'medium':
      return medium
    case 'large':
      return large
    default:
      return medium
  }
}

const StyledIconButton = styled(IconButton)`
  align-items: center;
  border-radius: ${th('radius')};
  border-width: ${props => (props.bordered ? 1 : 0)}px;
  border-style: solid;
  border-color: ${colorPropIf('active', 'lightestBlue2')};
  color: ${colorPropIf('active', 'gray')};
  cursor: pointer;
  font-weight: ${th('fontWeights.medium')};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colorProp} !important; /* fix SUI a:hover */
    border-color: ${colorPropUnless('active')};
    display: flex;
    transition: background-color 175ms ease-in-out, border 175ms ease-in-out;

    .Icon {
      color: inherit;
    }
  }

  ${sizes};
  ${spacing};
  ${props => props.label && 'width: auto'};
`
// eslint-disable-next-line react/no-multi-comp
class WithTooltip extends React.PureComponent {
  render() {
    const { tooltip, tooltipPosition, ...rest } = this.props
    return (
      <Tooltip position={tooltipPosition} tooltip={tooltip}>
        <StyledIconButton {...rest} />
      </Tooltip>
    )
  }
}

WithTooltip.propTypes = {
  tooltip: PropTypes.string,
  tooltipPosition: PropTypes.string,
}

WithTooltip.defaultProps = {
  tooltipPosition: 'bottom',
  tooltip: '',
}

export default WithTooltip
